import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { System } from '../../enums/System';
import {
  PublicOutlined, FastfoodOutlined, ScaleOutlined, AutoFixHighOutlined, StyleOutlined, LocalShipping,
  EditCalendarOutlined, AdminPanelSettingsOutlined, ViewModuleOutlined, DevicesOtherOutlined, LockOutlined,
  ScheduleSend, EditCalendar, EventRepeat, Task, Category, AccountTree, Newspaper, CurrencyExchange,
  ImageOutlined,
  Search,
} from '@mui/icons-material';
import {
  Users01, PackageCheck, CoinsSwap02, Calendar, User01, Announcement02,
  Settings01, Grid01, Tag03, CoinsHand,
  Truck01, ListIcon, Building02, ChevronRight,
} from '../../assets';
import { Role } from '../../enums/Role';
import { PageAccessStatus, canAccessPageStatus, shouldHideInDemo, shouldShowPreviewTag } from '../../utils/featureAccess';

export interface SettingItem {
  translationKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  href?: string;
  permissions?: Role[];
  systemIds: number[];
  requiredModule?: string;
  /** Completeness status for pages still in demo/preview. See src/utils/featureAccess.ts. */
  status?: PageAccessStatus;
  /** Hide this item entirely while Demo Mode is enabled (e.g. superseded by a demo equivalent). */
  hideInDemo?: boolean;
}

export interface SettingSection {
  titleKey: string;
  descriptionKey: string;
  items: SettingItem[];
  /** Completeness status for the whole section. See src/utils/featureAccess.ts. */
  status?: PageAccessStatus;
  /** Hide this whole section while Demo Mode is enabled. */
  hideInDemo?: boolean;
}

export const filterSettingSections = (
  sections: SettingSection[],
  canRenderItem: Function,
  t: Function,
  searchTerm: string,
): SettingSection[] => {
  const query = searchTerm.trim().toLowerCase();
  const translate: CallableFunction = t;
  const canAccessItem: CallableFunction = canRenderItem;

  const sectionsWithAccessibleItems = sections.map((section) => ({
    ...section,
    items: section.items.filter((item) => Boolean(canAccessItem(item))),
  }));

  if (!query) {
    return sectionsWithAccessibleItems.filter((section) => section.items.length > 0);
  }

  const sectionsWithMatchingTiles = sectionsWithAccessibleItems
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const itemTitle = String(translate(item.translationKey)).toLowerCase();
        const itemDescription = String(translate(item.descriptionKey, `Configure ${String(translate(item.translationKey)).toLowerCase()} settings and preferences.`)).toLowerCase();

        return itemTitle.includes(query) || itemDescription.includes(query);
      }),
    }))
    .filter((section) => section.items.length > 0);

  if (sectionsWithMatchingTiles.length > 0) {
    return sectionsWithMatchingTiles;
  }

  return sectionsWithAccessibleItems
    .map((section) => {
      const sectionTitle = String(translate(section.titleKey, section.titleKey)).toLowerCase();
      const sectionDescription = String(translate(section.descriptionKey, `Manage ${String(translate(section.titleKey)).toLowerCase()} settings and configurations.`)).toLowerCase();
      const sectionMatches = sectionTitle.includes(query) || sectionDescription.includes(query);

      if (!sectionMatches) {
        return {
          ...section,
          items: [],
        };
      }

      return section;
    })
    .filter((section) => section.items.length > 0);
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('drawer');
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo, selectedSystem, systems, hasPremiumModuleAccess } = useContext(UserContext);
  const [searchTerm, setSearchTerm] = useState('');

  const settingSections: SettingSection[] = [
    {
      titleKey: 'userAccessManagement',
      descriptionKey: 'userAccessManagementDesc',
      items: [
        {
          translationKey: 'manageApplicationRoles',
          descriptionKey: 'manageApplicationRolesDesc',
          icon: <Users01 />,
          href: '/role-management',
          permissions: ['RolesAdmin', 'RoleManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageUsers',
          descriptionKey: 'manageUsersDesc',
          icon: <User01 />,
          href: '/user-management',
          permissions: ['UserAdmin', 'UserManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'userAccessManagementPreview',
      descriptionKey: 'userAccessManagementPreviewDesc',
      status: 'preview',
      items: [
        //intentionally temporarily comment this until we have the role management pages ready for production
        // {
        //   translationKey: 'manageRolesPreview',
        //   descriptionKey: 'manageRolesPreviewDesc',
        //   icon: <Users01 />,
        //   href: '/role-management/roles',
        //   permissions: ['RolesAdmin', 'RoleManagementView'],
        //   systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        // },
        {
          translationKey: 'manageUsersPreview',
          descriptionKey: 'manageUsersPreviewDesc',
          icon: <User01 />,
          href: '/user-management/users',
          permissions: ['UserAdmin', 'UserManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'operationsAutomation',
      descriptionKey: 'operationsAutomationDesc',
      items: [
        {
          translationKey: 'countStockSchedule',
          descriptionKey: 'countStockScheduleDesc',
          icon: <PackageCheck />,
          href: '/stock-count-schedule',
          permissions: ['StockCountAdmin', 'StockLocationRead', 'StockCountScheduleRead', 'StockCountUomsRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'operationalDaySetup',
          descriptionKey: 'operationalDaySetupDesc',
          icon: <EditCalendar />,
          href: '/operational-days',
          permissions: ['OperationalDaysView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          status: 'demo',
        },
        {
          translationKey: 'scheduledTasks',
          descriptionKey: 'scheduledTasksDesc',
          icon: <EventRepeat />,
          href: '/task-schedules',
          permissions: ['TaskSchedulesView'],
          systemIds: [System.OrderlyForStores],
          status: 'demo',
        },
        {
          translationKey: 'taskTemplates',
          descriptionKey: 'taskTemplatesDesc',
          icon: <Task />,
          href: '/task-templates',
          permissions: ['TaskTemplatesViewUnrestricted'],
          systemIds: [System.OrderlyForStores],
          status: 'demo',
        },
        {
          translationKey: 'scheduledReports',
          descriptionKey: 'scheduledReportsDesc',
          permissions: ['AutomatedTasks'],
          icon: <ScheduleSend />,
          href: '/scheduled-reports',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'automatedWorkflows',
          descriptionKey: 'automatedWorkflowsDesc',
          icon: <AccountTree />,
          href: '/automated-workflows',
          permissions: ['AutomatedWorkflowsView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          status: 'demo',
        },
        {
          translationKey: 'autoshipments',
          descriptionKey: 'autoshipmentsDesc',
          icon: <LocalShipping />,
          href: '/manage-autoshipments/templates',
          permissions: ['AutoshipmentRead'],
          systemIds: [System.OrderlyForStores],
        },
      ],
    },
    {
      titleKey: 'contentManagement',
      descriptionKey: 'contentManagementDesc',
      items: [
        {
          translationKey: 'manageNotifications',
          descriptionKey: 'manageNotificationsDesc',
          icon: <Announcement02 />,
          href: '/manage-notifications',
          permissions: ['NotificationWrite'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'newsManagement',
          descriptionKey: 'newsManagementDesc',
          icon: <Newspaper />,
          href: '/news-management',
          permissions: ['NewsManagementView'],
          status: 'preview',
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'banners',
          descriptionKey: 'bannersDesc',
          icon: <ImageOutlined />,
          href: '/settings/content-management/banners',
          permissions: ['ContentManagementAdmin'],
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'itemTags',
          descriptionKey: 'itemTagsDesc',
          icon: <Tag03 />,
          permissions: ['ItemTagView', 'ItemTagAdmin'],
          href: '/item-tags',
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'systemConfiguration',
      descriptionKey: 'systemConfigurationDesc',
      items: [
        {
          translationKey: 'systemConfigurations',
          descriptionKey: 'systemConfigurationsDesc',
          icon: <Settings01 />,
          permissions: ['ConfigurationAdmin'],
          href: '/manage-configurations',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'featureFlags',
          descriptionKey: 'featureFlagsDesc',
          icon: <Settings01 />,
          permissions: ['FeatureFlagsManage'],
          href: '/feature-flags',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageTransactions',
          descriptionKey: 'manageTransactionsDesc',
          icon: <CoinsSwap02 />,
          href: '/manage-transactions',
          permissions: ['TransactionAdmin'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'manageTaskCategories',
          descriptionKey: 'manageTaskCategoriesDesc',
          icon: <Category />,
          href: '/manage-task-categories',
          permissions: ['TaskCategoriesView'],
          status: 'demo',
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'manageCategory',
          descriptionKey: 'manageCategoryDesc',
          icon: <Grid01 />,
          href: '/manage-categories',
          permissions: ['CategoryAdmin'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'uomTypes',
          descriptionKey: 'uomTypesDesc',
          icon: <ScaleOutlined />,
          permissions: ['UomTypeView'],
          href: '/uom-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },

      ],
    },
    {
      titleKey: 'procurement',
      descriptionKey: 'procurementDesc',
      status: 'preview',
      items: [
        {
          translationKey: 'orderChannels',
          descriptionKey: 'orderChannelsDesc',
          icon: <StyleOutlined />,
          permissions: ['OrderTypeView'],
          href: '/procurement/order-channels',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'deliverySchedules',
          descriptionKey: 'deliverySchedulesDesc',
          icon: <EditCalendarOutlined />,
          href: '/procurement/delivery-schedules',
          permissions: ['ODSRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'suppliers',
          descriptionKey: 'procurementSuppliersDesc',
          icon: <Truck01 />,
          permissions: ['SupplierView'],
          href: '/procurement/suppliers',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'catalogues',
          descriptionKey: 'procurementCataloguesDesc',
          icon: <ListIcon />,
          permissions: ['CatalogueView'],
          href: '/procurement/catalogues',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'promotions',
          descriptionKey: 'promotionsDesc',
          icon: <CoinsHand />,
          permissions: [ 'PromotionRead', 'PromotionAdmin'],
          href: '/procurement/promotions',
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'procurementCategories',
          descriptionKey: 'procurementCategoriesDesc',
          icon: <Tag03 />,
          permissions: ['CategoryView'],
          href: '/procurement/categories',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'products',
          descriptionKey: 'procurementProductsDesc',
          icon: <FastfoodOutlined />,
          permissions: ['ProductView'],
          href: '/procurement/products',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'productCatalogManagement',
      descriptionKey: 'productCatalogManagementDesc',
      hideInDemo: true,
      items: [
        {
          translationKey: 'orderTypes',
          descriptionKey: 'orderTypesDesc',
          icon: <StyleOutlined />,
          permissions: ['OrderTypeView'],
          href: '/order-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageDeliverySchedules',
          descriptionKey: 'manageDeliverySchedulesDesc',
          icon: <EditCalendarOutlined />,
          href: '/manage-delivery-schedules',
          permissions: ['ODSRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'deliveryPricing',
          descriptionKey: 'deliveryPricingDesc',
          icon: <CurrencyExchange />,
          href: '/settings/delivery-pricing',
          permissions: ['DeliveryPricingView'],
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'suppliers',
          descriptionKey: 'suppliersDesc',
          icon: <Truck01 />,
          permissions: ['SupplierView'],
          href: '/suppliers',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'catalogues',
          descriptionKey: 'cataloguesDesc',
          icon: <ListIcon />,
          permissions: ['CatalogueView'],
          href: '/catalogues',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'productCategories',
          descriptionKey: 'productCategoriesDesc',
          icon: <Tag03 />,
          permissions: ['CategoryView'],
          href: '/categories',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'products',
          descriptionKey: 'productsDesc',
          icon: <FastfoodOutlined />,
          permissions: ['ProductView'],
          href: '/products',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'promotions',
          descriptionKey: 'promotionsDesc',
          icon: <CoinsHand />,
          permissions: [ 'PromotionRead', 'PromotionAdmin'],
          href: '/promotions',
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'advancedFeatures',
      descriptionKey: 'advancedFeaturesDesc',
      items: [
        {
          translationKey: 'podSettings',
          descriptionKey: 'podSettingsDesc',
          icon: <AutoFixHighOutlined />,
          permissions: ['AIOrderingAdmin', 'AIOrderingConfiguration'],
          href: '/manage-pod-configurations',
          systemIds: [System.OrderlyForStores],
          requiredModule: 'Predictive Ordering Settings',
        },
        {
          translationKey: 'manageExchangeRates',
          descriptionKey: 'manageExchangeRatesDesc',
          icon: <CurrencyExchange />,
          href: '/manage-exchange-rates',
          permissions: ['ConfigurationAdmin'],
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'businessStructure',
      descriptionKey: 'businessStructureDesc',
      items: [
        {
          translationKey: 'instances',
          descriptionKey: 'instancesDesc',
          icon: <Grid01 />,
          permissions: ['InstanceView'],
          href: '/instances',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'markets',
          descriptionKey: 'marketsDesc',
          icon: <PublicOutlined />,
          permissions: ['MarketView'],
          href: '/markets',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        /*  {
            translationKey: 'storeGroups',
            descriptionKey: 'storeGroupsDesc',
            icon: <PublicOutlined />,
            permissions: ['NEWPERMISSION'],
            href: '/markets',
            systemIds: [System.OrderlyForStores],
          }, */
        {
          translationKey: 'stores',
          descriptionKey: 'storesDesc',
          icon: <Building02 />,
          permissions: ['StoreView'],
          href: '/stores',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'platformAdministration',
      descriptionKey: 'platformAdministrationDesc',
      items: [
        {
          translationKey: 'tenants',
          descriptionKey: 'tenantsDesc',
          icon: <AdminPanelSettingsOutlined />,
          href: '/admin/tenants',
          permissions: ['TenantView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'modules',
          descriptionKey: 'modulesDesc',
          icon: <ViewModuleOutlined />,
          href: '/admin/modules',
          permissions: ['ModuleView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'systems',
          descriptionKey: 'systemsDesc',
          icon: <DevicesOtherOutlined />,
          href: '/admin/systems',
          permissions: ['SystemView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'permissions',
          descriptionKey: 'permissionsDesc',
          icon: <LockOutlined />,
          href: '/admin/permissions',
          permissions: ['PermissionView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
  ];

  const pageAccessContext = useMemo(() => ({
    hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
    hasAdminPermission: hasPermissionTo(['Admin']),
  }), [hasPermissionTo]);

  const canRenderItem = (item: SettingItem): boolean => {
    const userCanAccessPage = !item.permissions || hasPermissionTo(item.permissions);
    const userCanAccessSystem = item.systemIds?.some((systemId) => systemId === selectedSystem?.id) ?? false;
    const systemExists = systems.some(x => x.id === 0) || userCanAccessSystem;
    const hasRequiredModule = !item.requiredModule || hasPremiumModuleAccess(item.requiredModule);
    const hasStatusAccess = canAccessPageStatus(item.status, pageAccessContext);
    // Demo Mode bypasses permission/system/module checks entirely for demo-flagged items —
    // hasStatusAccess already requires Demo Mode to be enabled for 'demo' status items.
    const bypassForDemo = item.status === 'demo' && hasStatusAccess;
    // Some items should be hidden entirely while Demo Mode is enabled — takes priority over
    // every other check.
    if (shouldHideInDemo(item.hideInDemo)) return false;

    return bypassForDemo || (userCanAccessPage && systemExists && hasRequiredModule && hasStatusAccess);
  };

  const handleItemClick = (item: SettingItem) => {
    if (item.href) {
      navigate(item.href);
    }
  };

  const visibleSections = useMemo(() => {
    const sectionsForCurrentDomain = settingSections.filter(
      (section) => !shouldHideInDemo(section.hideInDemo) && canAccessPageStatus(section.status, pageAccessContext),
    );

    const translate = (key: string, defaultValue?: string): string => {
      if (defaultValue) {
        return t(key, defaultValue);
      }

      return t(key);
    };

    return filterSettingSections(sectionsForCurrentDomain, canRenderItem, translate, searchTerm);
  }, [pageAccessContext, searchTerm, t, hasPermissionTo, selectedSystem, systems, hasPremiumModuleAccess]);


  const renderSettingsCard = (item: SettingItem) => (
    <Paper
      key={item.translationKey}
      elevation={0}
      sx={{
        p: 3,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 2,
        '&:hover': {
          backgroundColor: theme.palette.primary[50],
        },
      }}
      onClick={() => handleItemClick(item)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
        <Box
          sx={{
            width: 48,
            height: '100%',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          {item.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h6"
            sx={{ fontWeight: 600, mb: 1 }}>
            {t(item.translationKey)}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}>
            {t(item.descriptionKey, `Configure ${t(item.translationKey).toLowerCase()} settings and preferences.`)}
          </Typography>
        </Box>
        <ChevronRight
          style={{ color: theme.palette.primary.contrastText, marginTop: 1 }} />
      </Box>
    </Paper>
  );

  const renderSettingsSection = (section: SettingSection) => {
    const visibleItems = section.items;

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <Box key={section.titleKey}
        sx={{ mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography component="h5"
              sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t(section.titleKey, section.titleKey)}
            </Typography>
            {/* "demo" is an internal-only gate with no badge. "preview" is signposted, but not
                while Demo Mode is enabled — Demo Mode also grants access to preview content,
                and the customer-facing "Preview" label shouldn't leak into that flow. */}
            {shouldShowPreviewTag(section.status) && (
              <Chip
                label={t('preview')}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 400 }}
              />
            )}
          </Box>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}>
            {t(section.descriptionKey, `Manage ${t(section.titleKey).toLowerCase()} settings and configurations.`)}
          </Typography>
        </Box>
        <Grid container
          spacing={3}>
          {visibleItems.map((item) => (
            <Grid item
              xs={12}
              md={6}
              lg={4}
              key={item.translationKey}>
              {renderSettingsCard(item)}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };


  return (
    <PageArea>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <PageTitle>{t('settings')}</PageTitle>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mt: 1 }}>
            {t('settingsDescription', 'Manage your application settings, configurations, and preferences from this central location.')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t('settingsSearchPlaceholder', 'Search settings by section, tile title, or description')}
          inputProps={{ 'aria-label': t('settingsSearchAriaLabel', 'Search settings') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box>
        {visibleSections.length > 0
          ? visibleSections.map(renderSettingsSection)
          : (
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 2 }}
            >
              <Typography variant="textSM"
                color="text.secondary">
                {t('settingsSearchNoResults', 'No accessible settings match your search.')}
              </Typography>
            </Paper>
          )}
      </Box>
    </PageArea>
  );
};

export default SettingsPage;
